import React, { useMemo, useState } from "react";
import {
  OPTION_EVIDENCE_STATUSES,
  OPTION_RISK_LEVELS,
  OPTION_STATES,
} from "../optionsScanner/NinaOptionsScannerSchema";
import {
  compareOptionResults,
  evaluateOptionsData,
} from "../optionsScanner/NinaOptionsScannerEngine";
import {
  createOptionsReviewHandoff,
  openScannerReviewHandoff,
} from "../scanner/NinaScannerReviewHandoff";
import NinaOptionsPaperOrderPanel from "./NinaOptionsPaperOrderPanel";
import "./NinaOptionsScanner.css";

const REVIEW_NOW = Date.parse("2026-07-25T13:30:00.000Z");
const DEFAULT_LIMIT_OPTIONS = [10, 25, 30, 50];
const initialFilters = {
  search: "",
  submode: "same-day",
  type: "",
  strategy: "",
  state: "",
  risk: "",
  evidence: "",
  minimumConfidence: "",
  maximumSpread: "",
  minimumVolume: "",
  minimumOpenInterest: "",
  dteMin: "",
  dteMax: "",
  limit: "30",
  sort: "priority",
};

const SORTS = {
  symbol: (r) => r.symbol,
  direction: (r) => r.direction,
  type: (r) => r.contractType,
  setup: (r) => r.setup,
  expiration: (r) => Date.parse(`${r.expiration}T00:00:00Z`),
  dte: (r) => r.dte,
  strike: (r) => r.strike,
  underlying: (r) => r.underlyingPrice,
  bid: (r) => r.bid,
  ask: (r) => r.ask,
  spread: (r) => r.spread,
  premium: (r) => r.premium,
  volume: (r) => r.volume,
  openInterest: (r) => r.openInterest,
  activityRatio: (r) => r.volumeOpenInterestRatio,
  iv: (r) => r.impliedVolatility,
  delta: (r) => r.delta,
  breakeven: (r) => r.breakeven,
  distance: (r) => r.distanceToBreakevenPercent,
  liquidity: (r) =>
    r.signals.liquidity.status === "unavailable"
      ? null
      : r.signals.liquidity.score,
  catalyst: (r) => r.catalyst,
  smartFlow: (r) => r.scores.smartFlow,
  confidence: (r) => r.scores.confidence,
  state: (r) => OPTION_STATES.indexOf(r.state),
  risk: (r) => OPTION_RISK_LEVELS.indexOf(r.riskLevel),
  evidence: (r) => r.scores.completeness,
  updated: (r) => Date.parse(r.updatedAt),
};

const COLUMNS = [
  ["symbol", "Underlying symbol"],
  ["direction", "Direction"],
  ["type", "Contract type"],
  ["setup", "Strategy/setup"],
  ["expiration", "Expiration"],
  ["dte", "DTE"],
  ["strike", "Strike"],
  ["underlying", "Underlying price"],
  ["bid", "Contract bid"],
  ["ask", "Contract ask"],
  ["spread", "Contract spread"],
  ["premium", "Premium/contract"],
  ["volume", "Volume"],
  ["openInterest", "Open interest"],
  ["activityRatio", "Vol/OI"],
  ["iv", "Implied volatility"],
  ["delta", "Delta"],
  ["breakeven", "Breakeven"],
  ["distance", "Distance to breakeven"],
  ["liquidity", "Liquidity"],
  ["catalyst", "Catalyst"],
  ["smartFlow", "AI SmartFlow"],
  ["confidence", "AI Confidence"],
  ["state", "State"],
  ["risk", "Risk"],
  ["evidence", "Evidence"],
  ["updated", "Updated"],
];

export default function NinaOptionsScanner({
  snapshots = [],
  onOpenTradeReview,
  evaluatedData = null,
  defaultSubmode = "same-day",
  defaultLimit = "30",
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  dataMode = "authenticated-indicative",
}) {
  void dataMode;
  const [filters, setFilters] = useState(() => ({
    ...initialFilters,
    submode: defaultSubmode,
    limit: String(defaultLimit),
  }));
  const [direction, setDirection] = useState(null);
  const [view, setView] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 640
      ? "card"
      : "table",
  );
  const [expanded, setExpanded] = useState("");
  const [selectionError, setSelectionError] = useState("");
  const { results, errors, validCount, invalidCount } = useMemo(
    () => evaluatedData || evaluateOptionsData(snapshots, { now: REVIEW_NOW }),
    [evaluatedData, snapshots],
  );
  const strategies = useMemo(
    () =>
      [
        ...new Set(results.map((result) => result.setup).filter(Boolean)),
      ].sort(),
    [results],
  );

  const filteredAndSorted = useMemo(() => {
    const filtered = results.filter((result) => {
      const spread = result.spreadPercent;
      return (
        result.submode === filters.submode &&
        String(result.symbol || "")
          .toLowerCase()
          .includes(filters.search.trim().toLowerCase()) &&
        (!filters.type || result.contractType === filters.type) &&
        (!filters.strategy || result.setup === filters.strategy) &&
        (!filters.state || result.state === filters.state) &&
        (!filters.risk || result.riskLevel === filters.risk) &&
        (!filters.evidence || result.evidenceStatus === filters.evidence) &&
        meetsMinimum(result.scores?.confidence, filters.minimumConfidence) &&
        meetsMaximum(spread, filters.maximumSpread) &&
        meetsMinimum(result.volume, filters.minimumVolume) &&
        meetsMinimum(result.openInterest, filters.minimumOpenInterest) &&
        meetsMinimum(result.dte, filters.dteMin) &&
        meetsMaximum(result.dte, filters.dteMax)
      );
    });
    return filters.sort === "priority"
      ? [...filtered].sort(compareOptionResults)
      : [...filtered].sort((a, b) => {
          const accessor = SORTS[filters.sort] || SORTS.symbol;
          const av = accessor(a);
          const bv = accessor(b);
          if (!isSortableValue(av)) {
            return !isSortableValue(bv)
              ? a.stableKey.localeCompare(b.stableKey)
              : 1;
          }
          if (!isSortableValue(bv)) return -1;
          const comparison =
            typeof av === "string" ? av.localeCompare(bv) : av - bv;
          return (
            comparison * (direction === "asc" ? 1 : -1) ||
            a.stableKey.localeCompare(b.stableKey)
          );
        });
  }, [direction, filters, results]);

  const visible = useMemo(
    () => filteredAndSorted.slice(0, Number(filters.limit)),
    [filteredAndSorted, filters.limit],
  );

  const update = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const hasMore = visible.length < filteredAndSorted.length;
  const showMore = () => {
    const currentLimit = Number(filters.limit);
    const nextLimit = [...new Set(limitOptions.map(Number))]
      .filter((value) => Number.isFinite(value) && value > currentLimit)
      .sort((a, b) => a - b)[0];
    update(
      "limit",
      String(nextLimit || Math.max(filteredAndSorted.length, currentLimit)),
    );
  };

  const changeSubmode = (submode) => update("submode", submode);
  const reset = () => {
    setFilters({
      ...initialFilters,
      submode: defaultSubmode,
      limit: String(defaultLimit),
    });
    setDirection(null);
  };
  const sortBy = (key) => {
    setDirection((current) =>
      filters.sort === key && current === "asc" ? "desc" : "asc",
    );
    update("sort", key);
  };
  const chooseSymbol = (result) => {
    setSelectionError("");
    const response = openScannerReviewHandoff(
      createOptionsReviewHandoff(result),
      onOpenTradeReview,
    );
    setSelectionError(response.message);
  };

  return (
    <section
      className="ninaOptionsScanner"
      aria-labelledby="options-scanner-title"
    >
      <header className="optionsHero optionsPanel">
        <span>AUTHENTICATED ALPACA · INDICATIVE</span>
        <h1 id="options-scanner-title">AI Options Engine</h1>
        <p>
          Review authenticated indicative option contracts using the complete
          AI scanner workflow. Every contract remains WAIT until all required
          evidence is connected.
        </p>
        <div className="optionsSafetyNotice" role="note">
          <strong>AUTHENTICATED INDICATIVE OPTIONS DATA — NON-EXECUTABLE.</strong>
          <span>
            Real Alpaca indicative option-chain snapshots and contract metadata
            are used when available. Executable OPRA data is not connected. No
            order is prepared, staged, or placed.
          </span>
        </div>
      </header>

      <CollapsibleSection
        id="options-scanner-guidance"
        title="What is an options contract, and how should I read this scanner?"
        className="optionsEducation optionsPanel"
      >
        <div className="optionsEducationGrid">
          <article>
            <h2>Contract basics</h2>
            <p>
              A call generally gains value when its underlying rises; a put
              generally gains value when it falls. Strike is the contract price
              level, expiration is its final date, and DTE means days to
              expiration.
            </p>
            <p>
              One standard equity option usually represents 100 shares. A
              contract multiplier must be verified before premium or maximum
              loss is shown.
            </p>
          </article>
          <article>
            <h2>Price and liquidity</h2>
            <p>
              Bid is the buyer price, ask is the seller price, and spread is the
              difference. Volume is today&apos;s activity; open interest is the
              number of existing contracts. IV describes expected volatility,
              while delta estimates sensitivity to underlying movement.
            </p>
          </article>
          <article>
            <h2>Risk and assessment</h2>
            <p>
              Breakeven includes strike and premium. When a multiplier is
              validated, premium × multiplier estimates maximum loss for a
              purchased long contract. A strong stock setup can still have a
              poor option because of spread, liquidity, volatility, expiration,
              or distant breakeven.
            </p>
            <p>
              SmartFlow describes underlying alignment. Confidence describes
              evidence completeness and reliability. Neither predicts profit.
            </p>
            <p>
              Suitability starts at a neutral 50. Positive factors add signed
              weight, negative or conflicting factors subtract weight, and
              unavailable evidence receives the full conservative negative
              weight.
            </p>
          </article>
          <article>
            <h2>Supported contract structure</h2>
            <p>
              Phase 1 accepts purchased long single-leg calls and puts only.
              Estimated purchase cost uses ask × the validated contract
              multiplier, and maximum loss is the premium paid for that
              supported structure.
            </p>
            <p>
              Fees, slippage, taxes, and execution differences are excluded.
              Never apply this calculation to short, covered, protective
              stock-plus-option, credit, spread, or other multi-leg strategies.
            </p>
            <p>
              Directly selecting an underlying ticker carries that exact
              reviewed contract into Live Trading/Pit #3. It does not place,
              prepare, stage, confirm, or execute an order. Other row and
              control interactions do not open Pit #3.
            </p>
          </article>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id="options-scanner-definitions"
        title="Learn the states, risk levels, and evidence labels"
        className="optionsDefinitions optionsPanel"
      >
        <div className="optionsDefinitionGrid">
          <p>
            <strong>States:</strong> Ready to review passed the available review
            gates; Building is still developing; Monitoring is informational;
            Insufficient evidence is missing critical information; Risk blocked
            failed a safety gate.
          </p>
          <p>
            <strong>Evidence and risk:</strong> Evidence labels explain how a
            value is known. Unavailable means no value was invented. Low,
            Moderate, High, and Critical describe review risk—not expected
            profit.
          </p>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id="options-scanner-filters"
        title="Filters"
        className="optionsControls optionsPanel"
      >
        <div
          className="optionsSubmodes"
          role="tablist"
          aria-label="Options opportunity horizon"
        >
          <button
            role="tab"
            aria-selected={filters.submode === "same-day"}
            onClick={() => changeSubmode("same-day")}
          >
            Same-Day Momentum
          </button>
          <button
            role="tab"
            aria-selected={filters.submode === "longer-term"}
            onClick={() => changeSubmode("longer-term")}
          >
            Longer-Term Opportunities
          </button>
        </div>
        <p className="optionsModeNotice">
          Same-Day Momentum separates contracts with seven or fewer days to
          expiration. Longer-Term Opportunities organizes contracts with more
          time; neither view is a recommendation to enter or hold a contract.
        </p>
        <div className="optionsFilterGrid">
          <Control label="Search by symbol">
            <input
              value={filters.search}
              onChange={(e) => update("search", e.target.value)}
            />
          </Control>
          <Control label="Call or put">
            <select
              value={filters.type}
              onChange={(e) => update("type", e.target.value)}
            >
              <option value="">All</option>
              <option value="call">Call</option>
              <option value="put">Put</option>
            </select>
          </Control>
          <Control label="Strategy/setup">
            <select
              value={filters.strategy}
              onChange={(e) => update("strategy", e.target.value)}
            >
              <option value="">All validated strategies</option>
              {strategies.map((strategy) => (
                <option key={strategy} value={strategy}>
                  {strategy}
                </option>
              ))}
            </select>
          </Control>
          <Control label="State">
            <select
              value={filters.state}
              onChange={(e) => update("state", e.target.value)}
            >
              <option value="">All</option>
              {OPTION_STATES.map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </Control>
          <Control label="Risk">
            <select
              value={filters.risk}
              onChange={(e) => update("risk", e.target.value)}
            >
              <option value="">All</option>
              {OPTION_RISK_LEVELS.map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </Control>
          <Control label="Evidence">
            <select
              value={filters.evidence}
              onChange={(e) => update("evidence", e.target.value)}
            >
              <option value="">All</option>
              {OPTION_EVIDENCE_STATUSES.filter(
                (v) => v !== "simulated" && v !== "static-demo",
              ).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </Control>
          <Control label="Minimum Confidence">
            <input
              type="number"
              min="0"
              max="100"
              value={filters.minimumConfidence}
              onChange={(e) => update("minimumConfidence", e.target.value)}
            />
          </Control>
          <Control label="Maximum spread %">
            <input
              type="number"
              min="0"
              value={filters.maximumSpread}
              onChange={(e) => update("maximumSpread", e.target.value)}
            />
          </Control>
          <Control label="Minimum volume">
            <input
              type="number"
              min="0"
              value={filters.minimumVolume}
              onChange={(e) => update("minimumVolume", e.target.value)}
            />
          </Control>
          <Control label="Minimum open interest">
            <input
              type="number"
              min="0"
              value={filters.minimumOpenInterest}
              onChange={(e) => update("minimumOpenInterest", e.target.value)}
            />
          </Control>
          <Control label="Minimum DTE">
            <input
              type="number"
              min="0"
              value={filters.dteMin}
              onChange={(e) => update("dteMin", e.target.value)}
            />
          </Control>
          <Control label="Maximum DTE">
            <input
              type="number"
              min="0"
              value={filters.dteMax}
              onChange={(e) => update("dteMax", e.target.value)}
            />
          </Control>
          <Control label="Display limit">
            <select
              value={filters.limit}
              onChange={(e) => update("limit", e.target.value)}
            >
              {limitOptions.map((v) => (
                <option key={v} value={String(v)}>
                  {v}
                </option>
              ))}
            </select>
          </Control>
          <Control label="Sort">
            <select
              value={filters.sort}
              onChange={(e) => {
                update("sort", e.target.value);
                setDirection(e.target.value === "priority" ? null : "desc");
              }}
            >
              <option value="priority">Risk-first priority</option>
              {COLUMNS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Control>
        </div>
        <button type="button" className="optionsReset" onClick={reset}>
          Reset filters
        </button>
      </CollapsibleSection>

      {errors.length > 0 && (
        <div className="optionsError" role="alert">
          {errors.join(" ")}
        </div>
      )}
      {selectionError && (
        <div className="optionsError" role="alert">
          {selectionError}
        </div>
      )}

      <section className="optionsResults" aria-live="polite">
        <div className="optionsResultsHeader">
          <div>
            <h2>Ranked options opportunities</h2>
            <span>
              {visible.length} displayed • {validCount} authenticated indicative{" "}
              contracts evaluated • {invalidCount} invalid records excluded •{" "}
              Different symbols prioritized first • Source time shown per contract
            </span>
          </div>
          <div
            className="optionsViewToggle"
            role="group"
            aria-label="Options result view"
          >
            {hasMore && (
              <button type="button" onClick={showMore}>
                View more
              </button>
            )}
            <button
              aria-pressed={view === "table"}
              onClick={() => setView("table")}
            >
              Table View
            </button>
            <button
              aria-pressed={view === "card"}
              onClick={() => setView("card")}
            >
              Card View
            </button>
          </div>
        </div>
        {visible.length === 0 ? (
          <div className="optionsEmpty">
            No authenticated indicative contracts match these filters.
          </div>
        ) : view === "table" ? (
          <OptionsTable
            results={visible}
            expanded={expanded}
            onExpand={(key) =>
              setExpanded((current) => (current === key ? "" : key))
            }
            onSelect={chooseSymbol}
            sortKey={filters.sort}
            sortDirection={direction}
            onSort={sortBy}
          />
        ) : (
          visible.map((result) => (
            <OptionCard
              key={result.stableKey}
              result={result}
              expanded={expanded === result.stableKey}
              onExpand={() =>
                setExpanded((current) =>
                  current === result.stableKey ? "" : result.stableKey,
                )
              }
              onSelect={() => chooseSymbol(result)}
            />
          ))
        )}
      </section>
    </section>
  );
}

function Control({ label, children }) {
  return (
    <label className="optionsControl">
      <span>{label}</span>
      {React.cloneElement(children, { "aria-label": label })}
      <small>
        Uses validated available values; unavailable evidence remains
        unavailable.
      </small>
    </label>
  );
}

function SortHeading({ column, active, direction, onSort }) {
  return (
    <th
      aria-sort={
        active ? (direction === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button type="button" onClick={() => onSort(column[0])}>
        {column[1]} {active ? (direction === "asc" ? "▲" : "▼") : ""}
      </button>
    </th>
  );
}

function OptionsTable({
  results,
  expanded,
  onExpand,
  onSelect,
  sortKey,
  sortDirection,
  onSort,
}) {
  return (
    <div
      className="optionsTableContainer"
      tabIndex="0"
      aria-label="Scrollable options results table"
    >
      <table className="optionsTable">
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <SortHeading
                key={column[0]}
                column={column}
                active={sortKey === column[0]}
                direction={sortDirection}
                onSort={onSort}
              />
            ))}
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result) => {
            const open = expanded === result.stableKey;
            const panelId = `option-details-${safeId(result.stableKey)}`;
            return (
              <React.Fragment key={result.stableKey}>
                <tr
                  className={`optionsRow risk-${String(result.riskLevel || "high").toLowerCase()}`}
                  tabIndex="0"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => onExpand(result.stableKey)}
                  onKeyDown={(e) => {
                    if (
                      e.target === e.currentTarget &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault();
                      onExpand(result.stableKey);
                    }
                  }}
                >
                  <th className="optionsStickyId" scope="row">
                    <button
                      type="button"
                      aria-label={`Open ${result.symbol} in Live Trading for review`}
                      className="underlyingSelect"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(result);
                      }}
                    >
                      {result.symbol}
                    </button>
                    <span>{result.dataMode === "authenticated-indicative" ? result.dataLabel || "INDICATIVE" : "UNAVAILABLE"}</span>
                  </th>
                  <td>{result.direction}</td>
                  <td>{result.contractType}</td>
                  <td>{result.setup}</td>
                  <td>{result.expiration}</td>
                  <td>{result.dte}</td>
                  <td>{money(result.strike)}</td>
                  <td>{money(result.underlyingPrice)}</td>
                  <td>{money(result.bid)}</td>
                  <td>{money(result.ask)}</td>
                  <td>{money(result.spread, 4)}</td>
                  <td>{money(result.premium)}</td>
                  <td>{number(result.volume)}</td>
                  <td>{number(result.openInterest)}</td>
                  <td>{decimal(result.volumeOpenInterestRatio, 2, "×")}</td>
                  <td>{decimal(result.impliedVolatility, 1, "%")}</td>
                  <td>{decimal(result.delta, 2)}</td>
                  <td>{money(result.breakeven)}</td>
                  <td>{decimal(result.distanceToBreakevenPercent, 2, "%")}</td>
                  <td>
                    {result.signals.liquidity.status === "unavailable"
                      ? "Unavailable"
                      : `${result.signals.liquidity.status} ${result.signals.liquidity.score}`}
                  </td>
                  <td>{result.catalyst}</td>
                  <td>{number(result.scores.smartFlow)}</td>
                  <td>{number(result.scores.confidence)}</td>
                  <td>{result.state}</td>
                  <td>{result.riskLevel}</td>
                  <td>{decimal(result.scores.completeness, 0, "%")}</td>
                  <td>{time(result.updatedAt)}</td>
                  <td>
                    <button
                      aria-expanded={open}
                      aria-controls={panelId}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpand(result.stableKey);
                      }}
                    >
                      {open ? "Hide" : "Details"}
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr className="optionsDetailsRow">
                    <td colSpan={28}>
                      <OptionDetails id={panelId} result={result} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OptionCard({ result, expanded, onExpand, onSelect }) {
  const panelId = `option-card-details-${safeId(result.stableKey)}`;
  return (
    <article
      className={`optionCard risk-${String(result.riskLevel || "high").toLowerCase()}`}
    >
      <span className="simulatedBadge">
        {result.dataMode === "authenticated-indicative"
          ? result.dataLabel || "INDICATIVE"
          : "UNAVAILABLE"}
      </span>
      <h3>
        {result.symbol} · {result.setup}
      </h3>
      <p>
        {result.contractType} · {result.direction} · {result.expiration} ·{" "}
        {result.dte} DTE
      </p>
      <div className="optionCardMetrics">
        <span>
          Suitability <strong>{number(result.scores.suitability)}</strong>
        </span>
        <span>
          SmartFlow <strong>{number(result.scores.smartFlow)}</strong>
        </span>
        <span>
          Confidence <strong>{number(result.scores.confidence)}</strong>
        </span>
        <span>
          Risk <strong>{result.riskLevel}</strong>
        </span>
      </div>
      <div className="optionActions">
        <button
          aria-label={`Open ${result.symbol} in Live Trading for review`}
          onClick={onSelect}
        >
          {result.symbol}
        </button>
        <button
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onExpand}
        >
          {expanded ? "Hide details" : "View details"}
        </button>
      </div>
      {expanded && <OptionDetails id={panelId} result={result} />}
    </article>
  );
}

function OptionDetails({ id, result }) {
  const factors = Array.isArray(result.scores?.factors)
    ? result.scores.factors
    : [];
  const unavailable = factors
    .filter((factor) => factor.status === "unavailable")
    .map((factor) => factor.label);
  const conflicting = factors
    .filter((factor) => factor.status === "conflicting")
    .map((factor) => factor.label);
  const isAuthenticated = result.dataMode === "authenticated-indicative";
  const publicEvidenceStatus =
    result.evidenceStatus === "simulated" || result.evidenceStatus === "static-demo"
      ? "legacy non-live"
      : result.evidenceStatus;
  return (
    <section className="optionDetails" id={id}>
      <h3>Authenticated indicative contract details</h3>
      <dl className="optionContractFacts">
        <Fact label="Contract" value={result.contractId} />
        <Fact label="Underlying price" value={money(result.underlyingPrice)} />
        <Fact label="Strike" value={money(result.strike)} />
        <Fact label="Bid" value={money(result.bid)} />
        <Fact label="Ask" value={money(result.ask)} />
        <Fact label="Spread" value={money(result.spread, 4)} />
        <Fact label="Premium per contract" value={money(result.premium)} />
        <Fact label="Maximum loss" value={money(result.maxLoss)} />
        <Fact label="Volume" value={number(result.volume)} />
        <Fact label="Open interest" value={number(result.openInterest)} />
        <Fact
          label="Volume/open-interest ratio"
          value={decimal(result.volumeOpenInterestRatio, 2, "×")}
        />
        <Fact
          label="Implied volatility"
          value={decimal(result.impliedVolatility, 2, "%")}
        />
        <Fact label="Delta" value={decimal(result.delta, 4)} />
        <Fact label="Breakeven" value={money(result.breakeven)} />
        <Fact
          label="Distance to breakeven"
          value={decimal(result.distanceToBreakevenPercent, 2, "%")}
        />
        <Fact label="Liquidity" value={result.signals.liquidity.status} />
        <Fact label="Catalyst" value={result.catalyst} />
        <Fact
          label="Evidence"
          value={`${publicEvidenceStatus}; ${decimal(
            result.scores.completeness,
            0,
            "%",
          )} complete`}
        />
        <Fact label="Source" value={result.source} />
        <Fact label="As of" value={result.asOf} />
      </dl>
      <p>
        <strong>Why detected:</strong> {result.setup}. {result.catalystTiming}
      </p>
      <p>
        <strong>State:</strong> {result.state}.{" "}
        This authenticated indicative contract remains WAIT until every required
        safety gate is verified.
      </p>
      <p>
        <strong>Maximum-loss basis:</strong>{" "}
        {result.contractMultiplier === null
          ? "Unavailable because an authenticated contract multiplier was not supplied. No maximum loss is presented."
          : `Verified ${result.contractMultiplier}-share contract multiplier connected. Maximum loss remains unavailable because indicative pricing is non-executable and no order premium is prepared.`}
      </p>
      <p>
        <strong>Unavailable evidence:</strong>{" "}
        {unavailable.length ? unavailable.join(", ") : "None"}.
      </p>
      <p>
        <strong>Conflicting evidence:</strong>{" "}
        {conflicting.length ? conflicting.join(", ") : "None"}.
      </p>
      {result.blockReasons.length > 0 && (
        <div className="optionBlocks">
          <strong>Readiness blocked:</strong> {result.blockReasons.join(" ")}
        </div>
      )}
      <p>
        <strong>What must improve:</strong>{" "}
        {result.blockReasons.length
          ? result.blockReasons.join(" ")
          : "Continue independent review; no safety block is currently active."}
      </p>
      <p>
        <strong>Source:</strong> {result.source} · <strong>As of:</strong>{" "}
        {result.asOf} · <strong>Updated:</strong> {result.updatedAt}
      </p>
      <p>
        <strong>Alert stability:</strong> {result.occurrenceCount} occurrence(s)
        collapsed · latest seen {result.latestSeenAt}.
      </p>
      <div className="optionFactorList">
        {factors.map((factor) => (
          <div
            className={`optionFactor factor-${factor.status}`}
            key={factor.key}
          >
            <div>
              <strong>{factor.label}</strong>
              <span>
                {factor.status} · {decimal(factor.contribution, 2)}/
                {number(factor.weight)}
              </span>
            </div>
            <p>{factor.explanation}</p>
            <small>
              {factor.source} · {factor.evidenceStatus === "simulated" || factor.evidenceStatus === "static-demo" ? "legacy non-live" : factor.evidenceStatus}
            </small>
          </div>
        ))}
      </div>
      {isAuthenticated && <NinaOptionsPaperOrderPanel result={result} />}
    </section>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function CollapsibleSection({ id, title, className, children }) {
  const [open, setOpen] = useState(false);
  return (
    <section className={className}>
      <h2 className="optionsDisclosureHeading">
        <button
          id={`${id}-control`}
          type="button"
          className="optionsDisclosureButton"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((current) => !current)}
        >
          <span aria-hidden="true">{open ? "▾" : "▸"}</span>
          {title}
        </button>
      </h2>
      <div id={id} hidden={!open}>
        {children}
      </div>
    </section>
  );
}

const safeId = (value) =>
  String(value || "option").replace(/[^a-zA-Z0-9_-]/g, "-");
const finiteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const isSortableValue = (value) =>
  value !== null &&
  value !== undefined &&
  !(typeof value === "number" && Number.isNaN(value));

const meetsMinimum = (value, filter) => {
  if (filter === "") return true;
  const normalized = finiteNumber(value);
  return normalized !== null && normalized >= Number(filter);
};

const meetsMaximum = (value, filter) => {
  if (filter === "") return true;
  const normalized = finiteNumber(value);
  return normalized !== null && normalized <= Number(filter);
};

const number = (value) => {
  const normalized = finiteNumber(value);
  return normalized === null
    ? "Unavailable"
    : new Intl.NumberFormat("en-US").format(normalized);
};

const time = (value) => {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(timestamp)
    ? new Date(timestamp).toLocaleTimeString("en-US")
    : "Unavailable";
};

const decimal = (value, places = 2, suffix = "") => {
  const normalized = finiteNumber(value);
  return normalized === null
    ? "Unavailable"
    : `${normalized.toFixed(places)}${suffix}`;
};

const money = (value, places) => {
  const normalizedValue = finiteNumber(value);
  if (normalizedValue === null) return "Unavailable";
  const normalized = Object.is(normalizedValue, -0) ? 0 : normalizedValue;
  const decimalPlaces = places ?? (Math.abs(normalized) < 1 ? 4 : 2);
  return `$${normalized.toFixed(decimalPlaces)}`;
};
